/**
 * useWithingsAuth Hook
 * Manages automatic OAuth2 authentication flow for Withings
 */

import type { WithingsAuthConfig } from "../types/domain";

import { core } from "@tauri-apps/api";
import { listen } from "@tauri-apps/api/event";
import { useState, useEffect, useCallback, useRef } from "react";

import { debugHealth } from "@/domains/logging/utils/debugLog";

import { WithingsTokenService } from "../services/withings-token-service";
import { WithingsService } from "../services/withingsService";

export interface UseWithingsAuthState {
  /** Current authentication error */
  error: null | string;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Whether authentication is in progress */
  isAuthenticating: boolean;
  /** Whether service is initialized */
  isInitialized: boolean;
}

export interface UseWithingsAuthActions {
  /** Start OAuth2 authentication flow */
  authenticate: () => Promise<void>;
  /** Complete OAuth2 flow with authorization code */
  completeAuth: (code: string, state?: string) => Promise<void>;
  /** Disconnect from Withings */
  disconnect: () => Promise<void>;
  /** Initialize Withings service with custom config */
  initializeWithConfig: (config: WithingsAuthConfig) => Promise<void>;
  /** Sync data from Withings */
  syncData: () => Promise<void>;
}

export type UseWithingsAuthReturn = UseWithingsAuthState & UseWithingsAuthActions;

/**
 * Hook for managing Withings OAuth2 authentication
 * Provides automatic token refresh and seamless authentication
 *
 * @returns {UseWithingsAuthReturn} Authentication state and actions
 *
 */
export const useWithingsAuth = (): UseWithingsAuthReturn => {
  const [state, setState] = useState<UseWithingsAuthState>({
    error: null,
    isAuthenticated: false,
    isAuthenticating: false,
    isInitialized: false,
  });
  const isProcessingReference = useRef(false);
  const processingTimeoutReference = useRef<NodeJS.Timeout | null>(null);

  /**
   * Initialize Withings service
   */
  const initialize = useCallback(async () => {
    try {
      setState((previous) => ({ ...previous, error: null }));

      // Initialize Withings service
      await WithingsService.initialize();

      // Check if already authenticated
      const isAuthenticated = await WithingsTokenService.isAuthenticated();

      setState((previous) => ({
        ...previous,
        isAuthenticated,
        isInitialized: true,
      }));
    } catch {
      // NOTE: Don't show error to user, just mark as initialized
      setState((previous) => ({
        ...previous,
        isInitialized: true,
      }));
    }
  }, []);

  /**
   * Initialize Withings service with custom configuration
   */
  const initializeWithConfig = useCallback(async (config: WithingsAuthConfig) => {
    try {
      setState((previous) => ({ ...previous, error: null, isAuthenticating: true }));

      // Initialize with custom config
      await WithingsTokenService.initializeConfig(config);

      // Check if already authenticated
      const isAuthenticated = await WithingsTokenService.isAuthenticated();

      setState((previous) => ({
        ...previous,
        isAuthenticated,
        isAuthenticating: false,
        isInitialized: true,
      }));
    } catch (error) {
      setState((previous) => ({
        ...previous,
        error: error instanceof Error ? error.message : "Failed to initialize with config",
        isAuthenticating: false,
        isInitialized: true,
      }));
    }
  }, []);

  /**
   * Start OAuth2 authentication flow
   */
  const authenticate = useCallback(async () => {
    try {
      debugHealth("info", "withings", "Starting OAuth2 authentication flow", {
        file: "useWithingsAuth.ts",
        function: "authenticate",
      });

      setState((previous) => ({ ...previous, error: null, isAuthenticating: true }));

      // Debug configuration first
      await WithingsTokenService.debugConfig();

      // Generate authorization URL
      const authUrl = await WithingsTokenService.generateAuthUrl();
      debugHealth("info", "withings", "Generated authorization URL", {
        authUrl: authUrl,
        file: "useWithingsAuth.ts",
        function: "authenticate",
      });

      // Try to open browser for OAuth2 flow using Tauri opener plugin
      try {
        await core.invoke("shell_open", { request: { url: authUrl } });
        debugHealth("info", "withings", "Opened authorization URL in browser", {
          file: "useWithingsAuth.ts",
          function: "authenticate",
        });
      } catch (error) {
        debugHealth("error", "withings", "Failed to open browser, trying clipboard fallback", {
          error: error instanceof Error ? error.message : String(error),
          file: "useWithingsAuth.ts",
          function: "authenticate",
        });

        // Fallback: Copy URL to clipboard and show instructions
        try {
          await navigator.clipboard.writeText(authUrl);
          debugHealth("info", "withings", "Copied URL to clipboard as fallback", {
            file: "useWithingsAuth.ts",
            function: "authenticate",
          });

          setState((previous) => ({
            ...previous,
            error: `URL copied to clipboard! Please paste it in your browser: ${authUrl}`,
          }));
        } catch (clipboardError) {
          debugHealth(
            "error",
            "withings",
            "Failed to copy to clipboard, showing manual instructions",
            {
              error:
                clipboardError instanceof Error ? clipboardError.message : String(clipboardError),
              file: "useWithingsAuth.ts",
              function: "authenticate",
            },
          );

          setState((previous) => ({
            ...previous,
            error: `Please manually open this URL in your browser: ${authUrl}`,
          }));
        }
        return;
      }

      // Show success message with instructions
      setState((previous) => ({
        ...previous,
        error: null,
        isAuthenticating: false,
      }));

      debugHealth("info", "withings", "Authentication flow started successfully", {
        file: "useWithingsAuth.ts",
        function: "authenticate",
      });
    } catch (error) {
      debugHealth("error", "withings", "Failed to start authentication", {
        error: error instanceof Error ? error.message : String(error),
        file: "useWithingsAuth.ts",
        function: "authenticate",
      });

      setState((previous) => ({
        ...previous,
        error: error instanceof Error ? error.message : "Failed to start authentication",
        isAuthenticating: false,
      }));
    }
  }, []);

  /**
   * Sync data from Withings
   */
  const syncData = useCallback(async () => {
    try {
      setState((previous) => ({ ...previous, error: null }));

      // Sync measurements and user profile
      await WithingsService.syncMeasurementsToDatabase();
      await WithingsService.syncUserProfileToDatabase();
    } catch (error) {
      setState((previous) => ({
        ...previous,
        error: error instanceof Error ? error.message : "Failed to sync data",
      }));
    }
  }, []);

  /**
   * Complete OAuth2 flow with authorization code
   */
  const completeAuth = useCallback(
    async (code: string, state?: string) => {
      try {
        debugHealth("info", "withings", "Completing OAuth2 authentication flow", {
          code: code,
          file: "useWithingsAuth.ts",
          function: "completeAuth",
          state: state,
        });

        setState((previous) => ({ ...previous, error: null, isAuthenticating: true }));

        // Exchange authorization code for tokens
        await WithingsTokenService.exchangeCodeForToken(code, state);
        debugHealth("info", "withings", "Successfully exchanged authorization code for tokens", {
          file: "useWithingsAuth.ts",
          function: "completeAuth",
        });

        setState((previous) => ({
          ...previous,
          error: null,
          isAuthenticated: true,
          isAuthenticating: false,
        }));

        debugHealth("info", "withings", "Authentication completed successfully", {
          file: "useWithingsAuth.ts",
          function: "completeAuth",
        });

        // Auto-sync data after successful authentication
        try {
          await syncData();
          debugHealth("info", "withings", "Auto-sync completed after authentication", {
            file: "useWithingsAuth.ts",
            function: "completeAuth",
          });
        } catch (syncError) {
          debugHealth("error", "withings", "Auto-sync failed after authentication", {
            error: syncError instanceof Error ? syncError.message : String(syncError),
            file: "useWithingsAuth.ts",
            function: "completeAuth",
          });
          // Don't fail the entire auth process if sync fails
        }
      } catch (error) {
        debugHealth("error", "withings", "Failed to complete authentication", {
          error: error instanceof Error ? error.message : String(error),
          file: "useWithingsAuth.ts",
          function: "completeAuth",
        });

        // Provide more specific error messages
        let errorMessage = "Failed to complete authentication";
        if (error instanceof Error) {
          if (error.message.includes("Rate limit exceeded")) {
            errorMessage = "Rate limit exceeded. Please wait 10 seconds and try again.";
          } else if (error.message.includes("Invalid authorization code")) {
            errorMessage = "Invalid authorization code. Please try authenticating again.";
          } else if (error.message.includes("Invalid request parameters")) {
            errorMessage = "Configuration error. Please check your Withings settings.";
          } else {
            errorMessage = error.message;
          }
        }

        debugHealth("error", "withings", "Authentication failed with specific error message", {
          errorMessage: errorMessage,
          file: "useWithingsAuth.ts",
          function: "completeAuth",
        });

        setState((previous) => ({
          ...previous,
          error: errorMessage,
          isAuthenticating: false,
        }));
      }
    },
    [syncData],
  );

  // OAuth callback events removed - using manual code entry instead

  /**
   * Disconnect from Withings
   */
  const disconnect = useCallback(async () => {
    try {
      setState((previous) => ({ ...previous, error: null }));

      // Clear stored tokens
      await WithingsTokenService.clearTokens();

      setState((previous) => ({
        ...previous,
        isAuthenticated: false,
      }));
    } catch (error) {
      setState((previous) => ({
        ...previous,
        error: error instanceof Error ? error.message : "Failed to disconnect",
      }));
    }
  }, []);

  /**
   * Handle OAuth callback event
   */
  const handleOAuthCallback = useCallback(
    (event: { payload: string }) => {
      if (isProcessingReference.current) {
        return;
      }

      if (event.payload) {
        isProcessingReference.current = true;

        // Clear any existing timeout
        if (processingTimeoutReference.current) {
          clearTimeout(processingTimeoutReference.current);
        }

        // Set processing flag to false after 10 seconds to prevent permanent lock
        processingTimeoutReference.current = setTimeout(() => {
          isProcessingReference.current = false;
        }, 10_000);

        completeAuth(event.payload)
          .finally(() => {
            // Reset processing flag immediately after completion
            isProcessingReference.current = false;
            if (processingTimeoutReference.current) {
              clearTimeout(processingTimeoutReference.current);
              processingTimeoutReference.current = null;
            }
          })
          .catch(() => {
            // NOTE: Auth errors are handled by completeAuth
          });
      }
    },
    [completeAuth],
  );

  /**
   * Deep Link OAuth2 callback handler - Listen to backend events
   */
  useEffect(() => {
    let unlisten: (() => void) | undefined;

    void (async () => {
      try {
        unlisten = await listen<string>("withings-oauth-callback", handleOAuthCallback);
      } catch {
        // NOTE: Error during deep link processing - silently ignore to prevent UI disruption
        // Error is handled silently to prevent UI disruption
      }
    })();

    return () => {
      if (processingTimeoutReference.current) {
        clearTimeout(processingTimeoutReference.current);
      }
      if (unlisten) {
        try {
          unlisten();
        } catch {
          // NOTE: Error during cleanup - silently ignore
        }
      }
    };
  }, [handleOAuthCallback]);

  /**
   * Auto-refresh token when needed
   */
  useEffect(() => {
    if (!state.isInitialized) {
      initialize().catch(() => {
        // NOTE: Initialization errors are handled by initialize function
      });
      return;
    }

    if (state.isAuthenticated) {
      // Set up periodic token refresh check
      const checkAuthStatus = async () => {
        try {
          const isStillAuthenticated = await WithingsTokenService.isAuthenticated();
          if (!isStillAuthenticated) {
            setState((previous) => ({ ...previous, isAuthenticated: false }));
          }
        } catch {
          setState((previous) => ({
            ...previous,
            error: "Token refresh failed",
            isAuthenticated: false,
          }));
        }
      };

      const interval = setInterval(() => {
        void checkAuthStatus();
      }, 60_000); // Check every minute

      return () => {
        clearInterval(interval);
      };
    }
  }, [state.isInitialized, state.isAuthenticated, initialize]);

  return {
    ...state,
    authenticate,
    completeAuth,
    disconnect,
    initializeWithConfig,
    syncData,
  };
};
